import { APP_NAME } from '@clanfire/shared';
import { Ban, Check, MessageCircle, Search, UserMinus, UserPlus, Users, X } from '@/components/ui/icons';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import type { FriendRequestDTO, PublicUser } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { conversationApi, friendApi, userApi } from '@/api/endpoints';
import { TopBar } from '@/components/layout/TopBar';
import { Avatar, CountBadge, statusLabel } from '@/components/ui/Avatar';
import { Button, IconButton } from '@/components/ui/Button';
import { EmptyState, ErrorState } from '@/components/ui/Feedback';
import { Skeleton } from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';
import { useBlocks } from '@/stores/blocks';
import { useConversations, useFriends } from '@/stores/social';
import { confirm, toast, useUi } from '@/stores/ui';
import { usePresence, useUsers } from '@/stores/users';

type Tab = 'online' | 'all' | 'pending' | 'blocked' | 'add';

export function FriendsView() {
  const [tab, setTab] = useState<Tab>('online');
  const status = useFriends((s) => s.status);
  const pending = useFriends((s) => s.pendingCount);

  useEffect(() => {
    void useFriends.getState().load();
  }, []);

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'online', label: 'Online' },
    { id: 'all', label: 'All' },
    { id: 'pending', label: 'Pending' },
    { id: 'blocked', label: 'Blocked' },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <TopBar icon={<Users size={22} />} title="Friends" showSearch={false}>
        <div role="tablist" aria-label="Friend lists" className="mr-2 hidden items-center gap-1 sm:flex">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={cn('flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-sm font-semibold transition-colors', tab === t.id ? 'bg-active text-fg' : 'text-fg-muted hover:bg-hover hover:text-fg-soft')}
            >
              {t.label}
              {t.id === 'pending' && <CountBadge count={pending} />}
            </button>
          ))}
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'add'}
            onClick={() => setTab('add')}
            className={cn('rounded-lg px-2.5 py-1 text-sm font-semibold transition-colors', tab === 'add' ? 'bg-transparent text-success' : 'bg-success text-white hover:brightness-110')}
          >
            Add Friend
          </button>
        </div>
      </TopBar>

      {/* Compact tab bar for phones */}
      <div role="tablist" aria-label="Friend lists" className="flex gap-1 overflow-x-auto border-b border-line px-2 py-2 sm:hidden">
        {[...tabs, { id: 'add' as Tab, label: 'Add Friend' }].map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={cn('shrink-0 rounded-lg px-3 py-1.5 text-sm font-semibold', tab === t.id ? 'bg-active text-fg' : 'text-fg-muted')}
          >
            {t.label}
            {t.id === 'pending' && pending > 0 && <CountBadge count={pending} className="ml-1.5" />}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
        {tab === 'add' ? (
          <AddFriend />
        ) : tab === 'blocked' ? (
          <BlockedList />
        ) : status === 'error' ? (
          <ErrorState message="Could not load your friends." onRetry={() => void useFriends.getState().load(true)} />
        ) : status !== 'loaded' ? (
          <FriendSkeleton />
        ) : tab === 'pending' ? (
          <PendingList />
        ) : (
          <FriendList onlineOnly={tab === 'online'} onAdd={() => setTab('add')} />
        )}
      </div>
    </div>
  );
}

function FriendSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-full" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-40" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      ))}
    </div>
  );
}

function useOpenDm() {
  const navigate = useNavigate();
  return async (userId: string) => {
    try {
      const conversation = await conversationApi.open(userId);
      useConversations.getState().upsert(conversation);
      navigate(`/app/@me/${conversation.id}`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
}

function FriendList({ onlineOnly, onAdd }: { onlineOnly: boolean; onAdd: () => void }) {
  const friends = useFriends((s) => s.friends);
  const presences = usePresence((s) => s.statuses);
  const users = useUsers((s) => s.users);
  const [filter, setFilter] = useState('');
  const openDm = useOpenDm();
  const openModal = useUi((s) => s.openModal);

  const list = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return Object.values(friends)
      .map((f) => users[f.user.id] ?? f.user)
      .filter((u) => !onlineOnly || (presences[u.id] ?? 'offline') !== 'offline')
      .filter((u) => !q || u.displayName.toLowerCase().includes(q) || u.username.includes(q))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [friends, users, presences, onlineOnly, filter]);

  const remove = async (user: PublicUser) => {
    const ok = await confirm({ title: `Remove ${user.displayName}?`, body: 'You can always send them a new request later.', confirmLabel: 'Remove friend', danger: true });
    if (!ok) return;
    try {
      await friendApi.remove(user.id);
      useFriends.getState().removeFriend(user.id);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  if (Object.keys(friends).length === 0) {
    return (
      <EmptyState
        art="friends"
        title="No friends yet"
        body="Add someone by their username to start chatting one-on-one."
        action={<Button onClick={onAdd}>Add a friend</Button>}
      />
    );
  }

  return (
    <>
      <label className="relative mb-4 block">
        <Search size={16} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-fg-muted" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search friends"
          aria-label="Search friends"
          className="h-10 w-full rounded-xl border border-line bg-sunken pr-3 pl-9 text-sm text-fg placeholder:text-fg-muted focus:border-accent focus:outline-none"
        />
      </label>
      <h2 className="mb-2 text-[11px] font-bold tracking-wider text-fg-muted uppercase">
        {onlineOnly ? 'Online' : 'All friends'} — {list.length}
      </h2>
      {list.length === 0 ? (
        <p className="py-10 text-center text-sm text-fg-muted">{onlineOnly ? 'No friends are online right now.' : 'No friends match your search.'}</p>
      ) : (
        <ul className="divide-y divide-line">
          {list.map((user) => {
            const presence = presences[user.id] ?? 'offline';
            return (
              <li key={user.id} className="group flex items-center gap-3 rounded-lg py-2.5 transition-colors hover:bg-hover/50 sm:px-2">
                <button type="button" onClick={() => openModal({ type: 'profile', userId: user.id })} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                  <Avatar user={user} size={36} status={presence} ring="ring-surface" />
                  <span className="min-w-0">
                    <span className="block truncate font-semibold text-fg">
                      {user.displayName} <span className="hidden text-sm font-normal text-fg-muted group-hover:inline">@{user.username}</span>
                    </span>
                    <span className="block truncate text-xs text-fg-muted">{user.customStatus ?? statusLabel[presence]}</span>
                  </span>
                </button>
                <IconButton label={`Message ${user.displayName}`} onClick={() => void openDm(user.id)} className="bg-sunken">
                  <MessageCircle size={18} />
                </IconButton>
                <IconButton label={`Remove ${user.displayName}`} tone="danger" onClick={() => void remove(user)} className="bg-sunken">
                  <UserMinus size={18} />
                </IconButton>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

function BlockedList() {
  const blockedIds = useBlocks((s) => s.ids);
  const [state, setState] = useState<{ status: 'loading' | 'ready' | 'error'; users: PublicUser[] }>({ status: 'loading', users: [] });
  const [busy, setBusy] = useState<string | null>(null);

  const load = () =>
    userApi
      .blocks()
      .then((users) => setState({ status: 'ready', users }))
      .catch(() => setState((s) => ({ ...s, status: 'error' })));
  // Refetch whenever the set of blocked ids changes (e.g. blocked from a profile in another tab).
  const signature = Object.keys(blockedIds).sort().join(',');
  useEffect(() => {
    void load();
  }, [signature]);

  const unblock = async (user: PublicUser) => {
    setBusy(user.id);
    try {
      await userApi.unblock(user.id);
      useBlocks.getState().set(user.id, false);
      toast.success(`Unblocked ${user.displayName}.`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  if (state.status === 'error') return <ErrorState message="Could not load blocked users." onRetry={() => void load()} />;
  if (state.status === 'loading') return <FriendSkeleton />;
  if (state.users.length === 0) return <EmptyState icon={<Ban size={28} />} title="No one's blocked" body="Block someone from their profile to stop messages, friend requests and notifications from them." />;
  return (
    <section aria-label="Blocked users">
      <h2 className="mb-2 text-[11px] font-bold tracking-wider text-fg-muted uppercase">Blocked — {state.users.length}</h2>
      <ul className="divide-y divide-line">
        {state.users.map((u) => (
          <li key={u.id} className="flex items-center gap-3 py-2.5 sm:px-2">
            <Avatar user={u} size={36} />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-semibold">{u.displayName}</span>
              <span className="block truncate text-xs text-fg-muted">@{u.username}</span>
            </span>
            <Button variant="secondary" size="sm" loading={busy === u.id} onClick={() => void unblock(u)}>
              Unblock
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PendingList() {
  const requests = useFriends((s) => s.requests);
  const incoming = Object.values(requests).filter((r) => r.direction === 'incoming');
  const outgoing = Object.values(requests).filter((r) => r.direction === 'outgoing');

  if (incoming.length + outgoing.length === 0) {
    return <EmptyState icon={<UserPlus size={28} />} title="No pending requests" body="Friend requests you send or receive will show up here." />;
  }
  return (
    <div className="flex flex-col gap-6">
      {incoming.length > 0 && <RequestSection title={`Incoming — ${incoming.length}`} requests={incoming} />}
      {outgoing.length > 0 && <RequestSection title={`Outgoing — ${outgoing.length}`} requests={outgoing} />}
    </div>
  );
}

function RequestSection({ title, requests }: { title: string; requests: FriendRequestDTO[] }) {
  const [busy, setBusy] = useState<string | null>(null);
  const act = async (request: FriendRequestDTO, accept: boolean) => {
    setBusy(request.id);
    try {
      if (accept) {
        const friend = await friendApi.accept(request.id);
        useFriends.getState().addFriend(friend);
        toast.success(`You and ${request.user.displayName} are now friends!`);
      } else await friendApi.decline(request.id);
      useFriends.getState().removeRequest(request.id);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section aria-label={title}>
      <h2 className="mb-2 text-[11px] font-bold tracking-wider text-fg-muted uppercase">{title}</h2>
      <ul className="divide-y divide-line">
        {requests.map((r) => (
          <li key={r.id} className="flex items-center gap-3 py-2.5 sm:px-2">
            <Avatar user={r.user} size={36} />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-semibold">{r.user.displayName}</span>
              <span className="block truncate text-xs text-fg-muted">
                {r.direction === 'incoming' ? 'Incoming friend request' : 'Outgoing friend request'} · @{r.user.username}
              </span>
            </span>
            {r.direction === 'incoming' && (
              <IconButton label={`Accept ${r.user.displayName}`} disabled={busy === r.id} onClick={() => void act(r, true)} className="bg-sunken text-success! hover:bg-success! hover:text-white!">
                <Check size={18} />
              </IconButton>
            )}
            <IconButton label={r.direction === 'incoming' ? 'Ignore' : 'Cancel request'} tone="danger" disabled={busy === r.id} onClick={() => void act(r, false)} className="bg-sunken">
              <X size={18} />
            </IconButton>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AddFriend() {
  const [username, setUsername] = useState('');
  const [state, setState] = useState<{ kind: 'idle' | 'loading' | 'success' | 'error'; message?: string }>({ kind: 'idle' });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const value = username.trim().replace(/^@/, '');
    if (!value) return;
    setState({ kind: 'loading' });
    try {
      const res = await friendApi.request(value);
      if (res.status === 'accepted' && res.friend) {
        useFriends.getState().addFriend(res.friend);
        setState({ kind: 'success', message: `You and ${res.friend.user.displayName} are now friends!` });
      } else {
        if (res.request) useFriends.getState().addRequest(res.request);
        setState({ kind: 'success', message: `Success! Your friend request to @${value} was sent.` });
      }
      setUsername('');
    } catch (err) {
      setState({ kind: 'error', message: errorMessage(err) });
    }
  };

  return (
    <section className="max-w-2xl">
      <h2 className="text-lg font-bold">Add friend</h2>
      <p className="mt-1 text-sm text-fg-muted">You can add friends with their {APP_NAME} username.</p>
      <form onSubmit={submit} className="mt-4">
        <div
          className={cn(
            'flex items-center gap-2 rounded-xl border bg-sunken p-1.5 pl-4 transition-colors focus-within:border-accent',
            state.kind === 'error' ? 'border-danger' : state.kind === 'success' ? 'border-success' : 'border-line',
          )}
        >
          <input
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              if (state.kind !== 'loading') setState({ kind: 'idle' });
            }}
            placeholder="Enter a username, e.g. sam"
            aria-label="Username"
            autoFocus
            maxLength={33}
            className="h-10 min-w-0 flex-1 bg-transparent text-fg placeholder:text-fg-muted focus:outline-none"
          />
          <Button type="submit" disabled={!username.trim()} loading={state.kind === 'loading'}>
            Send request
          </Button>
        </div>
        {state.message && (
          <p role={state.kind === 'error' ? 'alert' : 'status'} className={cn('mt-2 text-sm', state.kind === 'error' ? 'text-danger' : 'text-success')}>
            {state.message}
          </p>
        )}
      </form>
    </section>
  );
}
